/**
 * Toast.tsx - Toast notification component
 * 
 * CHANGE HISTORY:
 * - 251025_1600 [pending] danielbeer: Initial creation
 * 
 * Non-blocking toast notifications
 */

import React, { useEffect } from 'react';
import { ToastConfig } from '../types';
import './Toast.css';

export interface ToastProps extends ToastConfig {
  id: string;
  onDismiss: (id: string) => void;
}

/**
 * Toast - Toast notification component
 * 
 * Features:
 * - Auto-dismiss after duration
 * - Manual dismiss button
 * - Type-based styling
 * - Optional action button
 * - Slide-in animation
 */
export const Toast: React.FC<ToastProps> = ({
  id,
  message,
  type = 'info',
  duration = 3000,
  action,
  onDismiss
}) => {
  useEffect(() => {
    if (!duration || duration <= 0) {
      return undefined;
    }

    const timer = setTimeout(() => {
      onDismiss(id);
    }, duration);

    return () => clearTimeout(timer);
  }, [id, duration, onDismiss]);

  const getIcon = (): string => {
    switch (type) {
      case 'success':
        return '✓';
      case 'error':
        return '✕';
      case 'warning':
        return '⚠';
      case 'info':
        return 'ℹ';
      case 'question':
        return '?';
      default:
        return 'ℹ';
    }
  };

  return (
    <div className={`promptr-toast promptr-toast-${type}`} role="alert">
      <div className="promptr-toast-icon">
        {getIcon()}
      </div>
      
      <div className="promptr-toast-content">
        <p className="promptr-toast-message">{message}</p>
        
        {action && (
          <button
            className="promptr-toast-action"
            onClick={() => {
              action.onClick();
              onDismiss(id);
            }}
          >
            {action.label}
          </button>
        )}
      </div>

      <button
        className="promptr-toast-close"
        onClick={() => onDismiss(id)}
        aria-label="Dismiss notification"
      >
        ×
      </button>
    </div>
  );
};
